import * as React from 'react'
import { useEffect, useState } from 'react'
import { css, styled, keyframes } from 'styled-components'
import { E, pipe, RD, TE } from '@packages/prelude'
import * as domain from '@packages/domain'
import { formatValidationErrors } from 'io-ts-reporters'

// Skeleton shimmer animation
const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`

// Styled components
const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;

  display: block;
`

const StyledImage = styled.img`
  width: 100%;
  height: 100%;

  object-fit: contain;
`

const Placeholder = styled.div<{ $loading?: boolean }>`
  background-color: #f5f5f5;
  width: 100%;
  height: 100%;
  padding-bottom: 75%; /* 4:3 aspect ratio */
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  ${({ $loading }) =>
    $loading === true &&
    css`
      background-color: #e2e2e2;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
        animation: ${shimmer} 1.5s infinite ease-in-out;
      }
    `}
`

const ErrorOverlay = styled.div`
  color: #e74c3c;
  padding: 10px;
  text-align: center;
  font-size: 0.8rem;
`

export interface ImageProps {
  imageId: number
  alt?: string
  className?: string
  size?: 'full' | 'medium' | 'thumbnail'
}

// Fetch image from WordPress REST API
const fetchImage = (imageId: number): TE.TaskEither<Error, domain.Image.Type> =>
  pipe(
    TE.tryCatch(
      () => fetch(`/wp-json/wp/v2/media/${imageId}`),
      () => new Error('Failed to fetch image data')
    ),
    TE.filterOrElse(
      response => response.ok,
      () => new Error('Failed to fetch image data')
    ),
    TE.chain(response =>
      TE.tryCatch(
        () => response.json(),
        () => new Error('Failed to parse image data')
      )
    ),
    TE.chainEitherK(data =>
      pipe(
        domain.WpMedia.Type.decode(data),
        E.mapLeft(errors => new Error(formatValidationErrors(errors).join('\n')))
      )
    ),
    TE.chainOptionK(() => new Error('Failed to process image data'))(domain.Image.fromWpMedia)
  )

export const Image: React.FC<ImageProps> = ({ imageId, alt = '', className, size = 'medium' }) => {
  const [image, setImage] = useState<RD.RemoteData<Error, domain.Image.Type>>(RD.initial)

  useEffect(() => {
    // Reset to loading state when imageId changes
    setImage(RD.pending)

    // Skip fetch if imageId is 0 (placeholder)
    if (imageId === 0) {
      setImage(RD.success(domain.Image.placeholder))
      return
    }

    // Fetch image data from WordPress API
    pipe(
      fetchImage(imageId),
      TE.match(
        error => setImage(RD.failure(error)),
        data => setImage(RD.success(data))
      )
    )()
  }, [imageId])

  return (
    <ImageContainer className={className}>
      {pipe(
        image,
        RD.fold(
          () => <Placeholder $loading />,
          () => <Placeholder $loading />,
          () => (
            <>
              <Placeholder />
              <ErrorOverlay>Failed to load image</ErrorOverlay>
            </>
          ),
          data => {
            const src = size === 'full' ? data.full : size === 'thumbnail' ? data.thumbnail : data.medium
            const imageAlt = alt || data.alt
            return <StyledImage src={src} alt={imageAlt} />
          }
        )
      )}
    </ImageContainer>
  )
}
